import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2, Download, Trash2, Plus, Save, FileText, FileSpreadsheet, FileDown, Upload,
  X, Phone, Mail, MapPin, History, ChevronDown, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import {
  getQuotes, getQuoteDetail, setQuoteStatus, downloadExport,
  getBookings, setBookingStatus, getSettings, saveSettings,
  getUsers, createUser, deleteUser, updateUser, getBlocked, addBlocked, removeBlocked,
  downloadBackup, restoreBackup, listStoredBackups, runBackup, restoreStoredBackup, downloadStoredBackup,
  admin as adminApi,
} from "../api";
import { T, CARD, INP, STATUS, STATUS_LABEL, QUOTE_STATUS_OPTIONS, BOOKING_STATUS_OPTIONS } from "./theme";

// Shared UI atoms
export const H = ({ children, right }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
    <h2 className="font-display font-bold tracking-tight text-2xl md:text-3xl" style={{ color: T.text }}>{children}</h2>
    {right && <div className="flex flex-wrap gap-2">{right}</div>}
  </div>
);

const btnGhost = { border: `1px solid ${T.border}`, color: T.text, background: "transparent" };
const btnGold = { background: `linear-gradient(135deg, ${T.gold}, ${T.goldHi})`, color: "#0A0A0C" };

function StatusPill({ status }) {
  const c = STATUS[status] || T.textDim;
  return (
    <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full"
      style={{ background: `${c}22`, color: c, border: `1px solid ${c}33` }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function StatusSelect({ value, options, onChange, tid }) {
  return (
    <select value={value || options[0]} onChange={(e) => onChange(e.target.value)} data-testid={tid}
      className="text-xs px-2.5 py-1.5 rounded-lg outline-none cursor-pointer"
      style={{ ...INP, borderColor: `${STATUS[value] || T.border}55`, color: STATUS[value] || T.text }}>
      {options.map((s) => <option key={s} value={s} style={{ background: T.card, color: T.text }}>{STATUS_LABEL[s] || s}</option>)}
    </select>
  );
}

// ---------------- Requests (Quotes) ----------------
export function RequestsPanel({ isClient }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => getQuotes().then((d) => { setQuotes(d); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const open = async (id) => {
    try {
      const q = await getQuoteDetail(id);
      setDetail(q);
      // fetch this customer's history
      const params = new URLSearchParams();
      if (q.email) params.set("email", q.email);
      if (q.phone) params.set("phone", q.phone);
      const hist = await adminApi.get(`/admin/customer-history?${params.toString()}`).then((r) => r.data).catch(() => []);
      setHistory(hist);
    } catch { toast.error("Failed to load request"); }
  };
  const changeStatus = async (id, status) => {
    try { await setQuoteStatus(id, status); toast.success("Status updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const filtered = filter === "all" ? quotes : quotes.filter((q) => (q.status || "new") === filter);
  const counts = QUOTE_STATUS_OPTIONS.reduce((acc, s) => ({ ...acc, [s]: quotes.filter((q) => (q.status || "new") === s).length }), {});

  return (
    <div data-testid="panel-requests">
      <H right={!isClient && (
        <>
          <button onClick={() => downloadExport("csv")} data-testid="export-csv" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm" style={btnGhost}><FileText size={15} /> CSV</button>
          <button onClick={() => downloadExport("xlsx")} data-testid="export-xlsx" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm" style={btnGhost}><FileSpreadsheet size={15} /> Excel</button>
          <button onClick={() => downloadExport("pdf")} data-testid="export-pdf" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold" style={btnGold}><FileDown size={15} /> PDF</button>
        </>
      )}>Estimate Requests</H>

      {/* Filter chips (workflow stages) */}
      <div className="flex flex-wrap gap-2 mb-5">
        <FilterChip label={`All (${quotes.length})`} active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all" />
        {QUOTE_STATUS_OPTIONS.map((s) => (
          <FilterChip key={s} label={`${STATUS_LABEL[s]} (${counts[s]})`} color={STATUS[s]}
            active={filter === s} onClick={() => setFilter(s)} testid={`filter-${s}`} />
        ))}
      </div>

      {loading ? (
        <div className="p-8 flex justify-center"><Loader2 className="animate-spin" style={{ color: T.gold }} /></div>
      ) : (
        <div className="overflow-x-auto rounded-2xl" style={CARD}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ color: T.gold, borderBottom: `1px solid ${T.border}` }}>
                {["Date", "Name", "Phone", "Service", "City", "Emergency", "Status", ""].map((h) =>
                  <th key={h} className="text-left px-4 py-3 font-semibold uppercase text-[11px] tracking-widest whitespace-nowrap">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.map((q) => (
                <tr key={q.id} data-testid="request-row" style={{ color: T.text, borderBottom: `1px solid ${T.border}` }}
                  className="hover:bg-white/[0.02] transition-colors">
                  <td className="px-4 py-3 text-xs" style={{ color: T.textDim }}>{(q.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 font-medium">{q.full_name}</td>
                  <td className="px-4 py-3"><a href={`tel:${q.phone}`} style={{ color: T.gold }}>{q.phone}</a></td>
                  <td className="px-4 py-3" style={{ color: T.textDim }}>{q.service}</td>
                  <td className="px-4 py-3" style={{ color: T.textDim }}>{q.city}</td>
                  <td className="px-4 py-3">{q.is_emergency ? <span style={{ color: T.danger, fontWeight: 700 }}>YES</span> : <span style={{ color: T.textFaint }}>No</span>}</td>
                  <td className="px-4 py-3">
                    <StatusSelect value={q.status || "new"} options={QUOTE_STATUS_OPTIONS} onChange={(v) => changeStatus(q.id, v)} tid={`status-${q.id}`} />
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => open(q.id)} data-testid="request-view"
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg" style={btnGhost}>View</button>
                  </td>
                </tr>
              ))}
              {!filtered.length && <tr><td colSpan={8} className="px-4 py-10 text-center" style={{ color: T.textFaint }}>No requests in this stage.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <AnimatePresence>
        {detail && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" }}
            onClick={() => { setDetail(null); setHistory(null); }}>
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
              className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl p-6"
              style={CARD} onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-display font-bold text-xl" style={{ color: T.text }}>{detail.full_name}</h3>
                  <StatusPill status={detail.status || "new"} />
                </div>
                <button onClick={() => { setDetail(null); setHistory(null); }} className="p-2 rounded-lg" style={btnGhost}><X size={16} /></button>
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm mb-4">
                <InfoRow icon={<Phone size={13} />} label="Phone" value={detail.phone} />
                <InfoRow icon={<Mail size={13} />} label="Email" value={detail.email} />
                <InfoRow icon={<MapPin size={13} />} label="Address"
                  value={`${detail.address || ""}, ${detail.city || ""}, ${detail.state || ""} ${detail.zip_code || ""}`} />
                <InfoRow label="Property" value={detail.property_type} />
                <InfoRow label="Service" value={detail.service} />
                <InfoRow label="Preferred date" value={detail.preferred_date} />
                <InfoRow label="Preferred time" value={detail.preferred_time} />
                <InfoRow label="Contact via" value={detail.contact_method} />
                <InfoRow label="Emergency" value={detail.is_emergency ? "YES" : "No"} />
              </div>
              {detail.description && (
                <div className="mt-4 p-4 rounded-lg" style={{ background: T.input, border: `1px solid ${T.border}` }}>
                  <div className="text-[10px] uppercase tracking-widest mb-1.5" style={{ color: T.gold }}>Description</div>
                  <p className="text-sm" style={{ color: T.text }}>{detail.description}</p>
                </div>
              )}
              {detail.photos?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {detail.photos.map((p, i) => <img key={i} src={p} alt="" className="w-24 h-24 rounded-lg object-cover" />)}
                </div>
              )}

              {history && history.length > 1 && (
                <div className="mt-6 pt-5 border-t" style={{ borderColor: T.border }}>
                  <div className="flex items-center gap-2 mb-3" style={{ color: T.gold }}>
                    <History size={14} />
                    <span className="text-xs font-semibold uppercase tracking-widest">Customer history ({history.length})</span>
                  </div>
                  <div className="space-y-2">
                    {history.map((h) => (
                      <div key={h.id} className="flex items-center justify-between text-xs px-3 py-2 rounded-lg" style={{ background: T.input }}>
                        <div style={{ color: T.textDim }}>{(h.created_at || "").slice(0, 10)}</div>
                        <div style={{ color: T.text }}>{h.service}</div>
                        <StatusPill status={h.status || "new"} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2 mt-6">
                <a href={`tel:${detail.phone}`} className="flex-1 rounded-lg py-2.5 font-semibold text-sm text-center" style={btnGold}>
                  Call customer
                </a>
                <a href={`mailto:${detail.email}`} className="flex-1 rounded-lg py-2.5 font-semibold text-sm text-center" style={btnGhost}>
                  Email customer
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex gap-3 py-1.5 border-b" style={{ borderColor: T.border }}>
      <span className="w-28 shrink-0 text-xs uppercase tracking-widest flex items-center gap-1.5" style={{ color: T.textFaint }}>
        {icon} {label}
      </span>
      <span className="text-sm" style={{ color: T.text }}>{value || "—"}</span>
    </div>
  );
}

function FilterChip({ label, active, onClick, color, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="text-xs px-3 py-1.5 rounded-full font-medium transition-all"
      style={{
        background: active ? (color ? `${color}22` : T.goldSoft) : "transparent",
        border: `1px solid ${active ? (color || T.gold) : T.border}`,
        color: active ? (color || T.gold) : T.textDim,
      }}>
      {label}
    </button>
  );
}

// ---------------- Bookings ----------------
export function BookingsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => getBookings().then((d) => { setRows(d); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const change = async (id, s) => { try { await setBookingStatus(id, s); toast.success("Status updated"); load(); } catch { toast.error("Failed"); } };
  return (
    <div data-testid="panel-bookings">
      <H>Appointments</H>
      {loading ? (
        <div className="p-8 flex justify-center"><Loader2 className="animate-spin" style={{ color: T.gold }} /></div>
      ) : (
        <div className="overflow-x-auto rounded-2xl" style={CARD}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ color: T.gold, borderBottom: `1px solid ${T.border}` }}>
                {["Date", "Window", "Name", "Phone", "Service", "Status"].map((h) =>
                  <th key={h} className="text-left px-4 py-3 font-semibold uppercase text-[11px] tracking-widest whitespace-nowrap">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id} style={{ color: T.text, borderBottom: `1px solid ${T.border}` }} className="hover:bg-white/[0.02]">
                  <td className="px-4 py-3 font-medium">{b.date}</td>
                  <td className="px-4 py-3" style={{ color: T.textDim }}>{b.time_window}</td>
                  <td className="px-4 py-3">{b.full_name}</td>
                  <td className="px-4 py-3"><a href={`tel:${b.phone}`} style={{ color: T.gold }}>{b.phone}</a></td>
                  <td className="px-4 py-3" style={{ color: T.textDim }}>{b.service}</td>
                  <td className="px-4 py-3">
                    <StatusSelect value={b.status || "requested"} options={BOOKING_STATUS_OPTIONS} onChange={(v) => change(b.id, v)} tid={`bstatus-${b.id}`} />
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={6} className="px-4 py-10 text-center" style={{ color: T.textFaint }}>No appointments yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------- Settings (Business Info) ----------------
const CLIENT_SETTING_FIELDS = [
  ["name", "Business name"], ["phone", "Phone (display)"], ["phoneRaw", "Phone (dial, +1...)"],
  ["email", "Quote destination email"], ["tagline", "Tagline"], ["sub", "Sub-headline"],
  ["years", "Years badge"], ["hours", "Business hours"],
  ["facebook", "Facebook URL"], ["google", "Google Business URL"],
  ["instagram", "Instagram URL"], ["website_url", "Website URL"], ["address", "Business address"],
  ["hero_image", "Hero image URL"], ["promo", "Promotion banner (optional)"],
];

export function SettingsPanel({ isClient }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { getSettings().then(setS).catch(() => {}); }, []);
  if (!s) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin" style={{ color: T.gold }} /></div>;
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => { setSaving(true); try { await saveSettings(s); toast.success("Settings saved"); } catch { toast.error("Failed"); } finally { setSaving(false); } };

  return (
    <div data-testid="panel-settings">
      <H right={
        <button onClick={save} disabled={saving} data-testid="settings-save"
          className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 font-semibold text-sm disabled:opacity-60"
          style={btnGold}>
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
        </button>
      }>Business Information</H>

      <div className="rounded-2xl p-6" style={CARD}>
        <div className="grid sm:grid-cols-2 gap-4">
          {CLIENT_SETTING_FIELDS.map(([k, label]) => (
            <div key={k}>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: T.textDim }}>{label}</label>
              <input data-testid={`set-${k}`} value={s[k] || ""} onChange={(e) => set(k, e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg text-sm outline-none" style={INP} />
            </div>
          ))}
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: T.textDim }}>About text</label>
            <textarea data-testid="set-about" rows={4} value={s.about_text || ""} onChange={(e) => set("about_text", e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg text-sm outline-none" style={INP} />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: T.textDim }}>Service areas (comma separated)</label>
            <input data-testid="set-areas" value={(s.areas || []).join(", ")}
              onChange={(e) => set("areas", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
              className="w-full px-3 py-2.5 rounded-lg text-sm outline-none" style={INP} />
          </div>
          <label className="flex items-center gap-2 text-sm" style={{ color: T.text }}>
            <input type="checkbox" data-testid="set-licensed" checked={!!s.licensed_insured}
              onChange={(e) => set("licensed_insured", e.target.checked)} className="w-4 h-4 accent-yellow-500" /> Show &quot;Licensed &amp; Insured&quot;
          </label>
          <label className="flex items-center gap-2 text-sm" style={{ color: T.text }}>
            <input type="checkbox" data-testid="set-emergency" checked={!!s.emergency_247}
              onChange={(e) => set("emergency_247", e.target.checked)} className="w-4 h-4 accent-yellow-500" /> Show &quot;24/7 Emergency&quot;
          </label>
        </div>
        {isClient && (
          <p className="mt-5 text-xs" style={{ color: T.textFaint }}>
            Note: Brand colors, logo, and SEO globals are managed by your platform administrator.
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------- Users ----------------
const ROLE_LABEL = { super_admin: "Super Admin", admin: "Administrator", editor: "Editor", client: "Client" };
const ROLE_COLOR = { super_admin: T.gold, admin: T.info, editor: T.success, client: T.purple };

export function UsersPanel({ me }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "editor" });
  const [edit, setEdit] = useState(null);
  const load = () => getUsers().then(setUsers).catch(() => toast.error("Admin only"));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.email || !form.password) { toast.error("Email and password required"); return; }
    try { await createUser(form); toast.success("User created"); setForm({ email: "", password: "", name: "", role: "editor" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Permanently remove this user?")) return; try { await deleteUser(id); toast.success("Removed"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const toggle = async (u) => { try { await updateUser(u.id, { active: !(u.active !== false) }); toast.success(u.active !== false ? "Deactivated" : "Activated"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const saveEdit = async () => {
    try { await updateUser(edit.id, { name: edit.name, role: edit.role, ...(edit.password ? { password: edit.password } : {}) }); toast.success("User updated"); setEdit(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="panel-users">
      <H>Team &amp; Roles</H>
      <div className="rounded-2xl p-5 mb-6" style={CARD}>
        <div className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: T.gold }}>Add a new user</div>
        <div className="grid sm:grid-cols-5 gap-2">
          <input placeholder="Name" data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="px-3 py-2 rounded-lg text-sm outline-none" style={INP} />
          <input placeholder="Email" data-testid="user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="px-3 py-2 rounded-lg text-sm outline-none" style={INP} />
          <input placeholder="Password" data-testid="user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="px-3 py-2 rounded-lg text-sm outline-none" style={INP} />
          <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="px-3 py-2 rounded-lg text-sm outline-none" style={INP}>
            {me === "super_admin" && <option value="super_admin">Super Admin</option>}
            <option value="admin">Administrator</option><option value="editor">Editor</option><option value="client">Client</option>
          </select>
          <button onClick={add} data-testid="user-add"
            className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 font-semibold text-sm" style={btnGold}>
            <Plus size={15} /> Add
          </button>
        </div>
      </div>

      <div className="grid gap-2">
        {users.map((u) => (
          <div key={u.id} data-testid="user-item" className="flex items-center gap-4 p-4 rounded-xl" style={CARD}>
            <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm shrink-0"
              style={{ background: `${ROLE_COLOR[u.role]}22`, color: ROLE_COLOR[u.role] || T.gold }}>
              {(u.name || u.email || "?").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate" style={{ color: T.text }}>{u.name || u.email}</div>
              <div className="text-xs truncate" style={{ color: T.textFaint }}>{u.email}</div>
            </div>
            <span className="text-[10px] px-2 py-1 rounded-full font-bold uppercase tracking-widest"
              style={{ background: `${ROLE_COLOR[u.role]}22`, color: ROLE_COLOR[u.role] || T.gold }}>{ROLE_LABEL[u.role] || u.role}</span>
            <span className="text-[10px] px-2 py-1 rounded-full font-semibold"
              style={{ background: T.input, color: u.active === false ? T.danger : T.success }}>{u.active === false ? "Inactive" : "Active"}</span>
            <button onClick={() => setEdit({ ...u, password: "" })} data-testid="user-edit" className="px-3 py-1.5 rounded-lg text-xs" style={btnGhost}>Edit</button>
            <button onClick={() => toggle(u)} data-testid="user-toggle" className="px-3 py-1.5 rounded-lg text-xs" style={btnGhost}>{u.active === false ? "Activate" : "Deactivate"}</button>
            <button onClick={() => del(u.id)} data-testid="user-delete" className="p-2 rounded-lg" style={{ color: T.danger }}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {edit && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.7)" }} onClick={() => setEdit(null)}>
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
              className="w-full max-w-md rounded-2xl p-6" style={CARD} data-testid="user-editor" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold text-lg" style={{ color: T.text }}>Edit user</h3>
                <button onClick={() => setEdit(null)} className="p-2 rounded-lg" style={btnGhost}><X size={16} /></button>
              </div>
              <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: T.textDim }}>Name</label>
              <input value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                className="w-full mb-3 px-3 py-2 rounded-lg text-sm outline-none" style={INP} />
              <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: T.textDim }}>Role</label>
              <select value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })}
                className="w-full mb-3 px-3 py-2 rounded-lg text-sm outline-none" style={INP}>
                {me === "super_admin" && <option value="super_admin">Super Admin</option>}
                <option value="admin">Administrator</option><option value="editor">Editor</option><option value="client">Client</option>
              </select>
              <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: T.textDim }}>New password (optional)</label>
              <input type="password" value={edit.password || ""} onChange={(e) => setEdit({ ...edit, password: e.target.value })}
                className="w-full mb-4 px-3 py-2 rounded-lg text-sm outline-none" style={INP} />
              <div className="flex gap-2">
                <button onClick={saveEdit} data-testid="user-save" className="flex-1 rounded-lg py-2.5 font-semibold text-sm" style={btnGold}>Save</button>
                <button onClick={() => setEdit(null)} className="px-4 rounded-lg text-sm" style={btnGhost}>Cancel</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------- Calendar / Blocked dates ----------------
export function CalendarPanel() {
  const [dates, setDates] = useState([]);
  const [d, setD] = useState("");
  const load = () => getBlocked().then((x) => setDates(x.map((i) => i.date))).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => { if (!d) return; try { await addBlocked(d); setD(""); load(); toast.success("Date blocked"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const rm = async (date) => { try { await removeBlocked(date); load(); toast.success("Unblocked"); } catch { toast.error("Failed"); } };
  return (
    <div data-testid="panel-calendar">
      <H>Calendar &amp; Availability</H>
      <div className="rounded-2xl p-6" style={CARD}>
        <p className="mb-4 text-sm" style={{ color: T.textDim }}>
          Sundays are automatically blocked. Add specific holidays or unavailable dates to prevent bookings.
        </p>
        <div className="flex gap-2 mb-6 max-w-md">
          <input type="date" data-testid="block-date-input" value={d} onChange={(e) => setD(e.target.value)}
            className="flex-1 px-3 py-2 rounded-lg text-sm outline-none" style={INP} />
          <button onClick={add} data-testid="block-date-add"
            className="inline-flex items-center gap-2 rounded-lg px-4 py-2 font-semibold text-sm" style={btnGold}>
            <Plus size={15} /> Block date
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {dates.map((x) => (
            <span key={x} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs"
              style={{ background: T.input, border: `1px solid ${T.border}`, color: T.text }}>
              {x} <button onClick={() => rm(x)} style={{ color: T.danger }}><Trash2 size={12} /></button>
            </span>
          ))}
          {!dates.length && <p className="text-sm" style={{ color: T.textFaint }}>No blocked dates.</p>}
        </div>
      </div>
    </div>
  );
}

// ---------------- Backup ----------------
export function BackupPanel() {
  const [busy, setBusy] = useState(false);
  const [stored, setStored] = useState([]);
  const [running, setRunning] = useState(false);
  const loadStored = () => listStoredBackups().then(setStored).catch(() => {});
  useEffect(() => { loadStored(); }, []);
  const onRestore = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!window.confirm("Restoring will REPLACE current content. Continue?")) return;
    setBusy(true);
    try { const data = JSON.parse(await file.text()); await restoreBackup(data); toast.success("Restored. Reload to see changes."); }
    catch (e2) { toast.error(e2.response?.data?.detail || "Restore failed"); }
    finally { setBusy(false); }
  };
  const runNow = async () => {
    setRunning(true);
    try { await runBackup(); toast.success("Backup created"); loadStored(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setRunning(false); }
  };
  const restoreOne = async (id) => {
    if (!window.confirm("Restore this snapshot? Current content will be replaced.")) return;
    try { await restoreStoredBackup(id); toast.success("Restored"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="panel-backup">
      <H right={
        <button onClick={runNow} disabled={running} data-testid="backup-run" className="inline-flex items-center gap-2 rounded-lg px-4 py-2 font-semibold text-sm" style={btnGold}>
          {running ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Run backup now
        </button>
      }>Backup &amp; Restore</H>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="p-6 rounded-2xl" style={CARD}>
          <Download style={{ color: T.gold }} />
          <h3 className="mt-3 font-semibold" style={{ color: T.text }}>Download backup</h3>
          <p className="text-sm mt-1 mb-4" style={{ color: T.textDim }}>Export all content, requests and settings as JSON.</p>
          <button onClick={downloadBackup} data-testid="backup-download" className="rounded-lg px-5 py-2.5 font-semibold text-sm" style={btnGold}>Download</button>
        </div>
        <div className="p-6 rounded-2xl" style={CARD}>
          <Upload style={{ color: T.gold }} />
          <h3 className="mt-3 font-semibold" style={{ color: T.text }}>Restore backup</h3>
          <p className="text-sm mt-1 mb-4" style={{ color: T.textDim }}>Upload a backup JSON. This replaces current data.</p>
          <label className="inline-block rounded-lg px-5 py-2.5 font-semibold text-sm cursor-pointer" style={btnGhost}>
            {busy ? "Restoring..." : "Choose file"}
            <input type="file" accept="application/json" className="hidden" onChange={onRestore} data-testid="backup-restore" />
          </label>
        </div>
      </div>

      <div className="rounded-2xl p-5" style={CARD}>
        <h3 className="font-display font-bold uppercase text-sm tracking-widest mb-4" style={{ color: T.text }}>
          Automatic snapshots (last 10)
        </h3>
        <div className="space-y-2">
          {stored.map((b) => (
            <div key={b.id} className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm" style={{ background: T.input }}>
              <span className="text-xs" style={{ color: T.textFaint }}>{(b.created_at || "").slice(0, 16).replace("T", " ")}</span>
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: T.goldSoft, color: T.gold }}>{b.type}</span>
              <span className="flex-1 text-xs" style={{ color: T.textDim }}>{Math.round((b.size || 0) / 1024)} KB</span>
              <button onClick={() => downloadStoredBackup(b.id)} className="text-xs px-3 py-1 rounded-lg" style={btnGhost}>Download</button>
              <button onClick={() => restoreOne(b.id)} className="text-xs px-3 py-1 rounded-lg font-semibold" style={btnGold}>Restore</button>
            </div>
          ))}
          {!stored.length && <p className="text-sm" style={{ color: T.textFaint }}>No snapshots yet. Click &quot;Run backup now&quot; to create one.</p>}
        </div>
      </div>
    </div>
  );
}
